import PotaParkPouchRepo from '@/repos/PotaParkPouchRepo';
import PotaParkJsonRepo from '@/repos/PotaParkJsonRepo';
import { type PotaParkType } from "@/types";
import { potaParksLogger } from "@/utils/loggers";
import { useOnline } from '@vueuse/core'
import HamrsApiService from '@/services/HamrsApiService';

/**
 * Safely access window.parksData with proper error handling
 * @throws Error if parks data is not available
 * @returns The parks data array
 */
function getParksDataSafely(): PotaParkType[] {
  if (!window.parksData) {
    throw new Error('Static parks data not available - app may still be initializing');
  }
  return window.parksData;
}

/**
 * Calculate the great-circle distance between two points on Earth using the Haversine formula
 * @param lat1 Latitude of first point in degrees
 * @param lon1 Longitude of first point in degrees  
 * @param lat2 Latitude of second point in degrees
 * @param lon2 Longitude of second point in degrees
 * @returns Distance in kilometers
 */
function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // Earth's radius in kilometers

  // Convert degrees to radians
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const lat1Rad = lat1 * Math.PI / 180;
  const lat2Rad = lat2 * Math.PI / 180;

  // Haversine formula
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1Rad) * Math.cos(lat2Rad) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in kilometers
}

const PotaParkService = {
  async initialize(): Promise<void> {
    try {
      await PotaParkPouchRepo.initializeParksData();
    } catch (error) {
      potaParksLogger('Error initializing parks service', error);
      throw error;
    }
  },

  // Non-blocking initialization that runs in background
  initializeInBackground(): void {
    // Fire and forget - don't await this
    this.initialize().catch(error => {
      potaParksLogger('Background initialization failed', error);
    });
  },

  async fetchById(parkId: string): Promise<PotaParkType> {
    try {
      const normId = parkId.toUpperCase();
      potaParksLogger(`Fetching park with ID: ${normId}`);

      // First try local database (fastest)
      try {
        const park = await PotaParkPouchRepo.findById(normId);
        if (park) {
          potaParksLogger('Park found in local database');
          return park;
        }
      } catch (dbError) {
        // Database might not be initialized yet, continue to static data
        potaParksLogger('Local database not ready, trying static data', dbError);
      }

      // Try static data immediately (always available)
      try {
        potaParksLogger('Trying static data');
        const park = PotaParkJsonRepo.fetchById(normId);

        if (park) {
          // Try to cache to database in background (don't await)
          PotaParkPouchRepo.create(park).catch(error => {
            potaParksLogger('Failed to cache park to database (database may be initializing)', error);
          });

          return park;
        } else {
          potaParksLogger(`Park ${parkId} not found in static data`);
        }
      } catch (staticError) {
        potaParksLogger('Error accessing static data', staticError);
      }

      // If now online, try API as last resort
      if (useOnline().value) {
        potaParksLogger('Park not found locally, fetching from API');
        try {
          const park = await HamrsApiService.fetchPark(normId);
          // Try to cache the park locally for future use (don't await)
          PotaParkPouchRepo.create(park).catch(error => {
            potaParksLogger('Failed to cache API result to database', error);
          });
          return park;
        } catch (apiError) {
          potaParksLogger('API fetch failed', apiError);
        }
      }

      throw new Error(`Park ${parkId} not found`);
    } catch (error) {
      potaParksLogger('Error fetching park', error);
      throw error;
    }
  },

  async searchParks(query: string, limit: number = 50): Promise<PotaParkType[]> {
    try {
      // Try database first
      const dbResults = await PotaParkPouchRepo.search(query, limit);
      if (dbResults.length > 0) {
        return dbResults;
      }
      return []; // Add explicit return to fix lint error
    } catch (error) {
      potaParksLogger('Database search failed, falling back to static data', error);

      // Fallback to static data search
      try {
        const parksData = getParksDataSafely();
        const upperQuery = query.toUpperCase();
        const results = parksData
          .filter(park =>
            park.name.toUpperCase().includes(upperQuery) ||
            park.id?.toUpperCase().includes(upperQuery)
          )
          .slice(0, limit);

        return results;
      } catch (staticError) {
        potaParksLogger('Static data search also failed', staticError);
        throw error; // Throw original database error
      }
    }
  },

  async findParksByLocation(location: string): Promise<PotaParkType[]> {
    try {
      return await PotaParkPouchRepo.findByLocation(location);
    } catch (error) {
      potaParksLogger('Database location search failed, falling back to static data', error);

      // Fallback to static data
      try {
        const parksData = getParksDataSafely();
        const results = parksData.filter(park =>
          park.location.toUpperCase() === location.toUpperCase()
        );

        return results;
      } catch (staticError) {
        potaParksLogger('Static data location search also failed', staticError);
        throw error;
      }
    }
  },

  async findNearbyParks(latitude: number, longitude: number, radiusKm: number = 50): Promise<PotaParkType[]> {
    try {
      return await PotaParkPouchRepo.findNearby(latitude, longitude, radiusKm);
    } catch (error) {
      potaParksLogger('Database nearby search failed, falling back to static data', error);

      // Fallback to static data with accurate distance calculation
      try {
        // Use Haversine formula for accurate great-circle distance calculation
        const parksData = getParksDataSafely();
        const results = parksData.filter(park => {
          const distance = haversineDistance(latitude, longitude, park.latitude, park.longitude);
          return distance <= radiusKm && park.active;
        }).slice(0, 100);

        return results;
      } catch (staticError) {
        potaParksLogger('Static data nearby search also failed', staticError);
        throw error;
      }
    }
  },

  async getParksCount(): Promise<number> {
    try {
      return await PotaParkPouchRepo.getCount();
    } catch (error) {
      potaParksLogger('Error getting parks count', error);
      return 0;
    }
  },

  async checkForUpdates(): Promise<void> {
    try {
      await PotaParkPouchRepo.checkForUpdates();
    } catch (error) {
      potaParksLogger('Error checking for updates', error);
      throw error;
    }
  },

  async forceSync(): Promise<void> {
    try {
      await PotaParkPouchRepo.forceSync();
    } catch (error) {
      potaParksLogger('Error forcing sync', error);
      throw error;
    }
  }
};

export default PotaParkService;